import React, { useState, useEffect } from "react";
import "./VerificationModal.css";

const VerificationModal = ({
  show,
  onHide,
  onVerified,
  userId,
  userEmail,
  isForLoyalty,
  onSkipVerification,
}) => {
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState(userEmail || "");
  const [contactNumber, setContactNumber] = useState("");
  const [age, setAge] = useState("");
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (show) {
      setEmail(userEmail || "");
      setContactNumber("");
      setAge("");
      setUserName("");
      setAddress("");
      setError("");
      setFieldErrors({});
      setEmailSent(false);
    }
  }, [show, userEmail]);

  const validateFields = () => {
    const errors = {};

    if (!userName.trim()) {
      errors.userName = "User name is required";
    } else if (userName.trim().length < 2) {
      errors.userName = "Name must be at least 2 characters";
    }

    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address";
    }

    if (!contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    } else if (!/^\d{10,15}$/.test(contactNumber.replace(/\s/g, ""))) {
      errors.contactNumber = "Enter a valid contact number";
    }

    if (!age.trim()) {
      errors.age = "Age is required";
    } else if (parseInt(age) < 18) {
      errors.age = "You must be 18 or older to place this order";
    } else if (parseInt(age) > 120) {
      errors.age = "Please enter a valid age";
    }

    if (!address.trim()) {
      errors.address = "Address is required";
    } else if (address.trim().length < 5) {
      errors.address = "Please enter a complete address";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SECRET}/api/GoTasteUser/SendVerificationEmail`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: userId,
            userName: userName.trim(),
            email: email.trim(),
            phoneNumber: contactNumber.replace(/\s/g, ""),
            age: parseInt(age, 10),
            address: address.trim(),
          }),
        },
      );

      if (response.ok) {
        setEmailSent(true);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to send verification email.");
      }
    } catch (err) {
      console.error("Verification submission error:", err);
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (e) => {
    setUserName(e.target.value);
    if (fieldErrors.userName) {
      setFieldErrors((prev) => ({ ...prev, userName: "" }));
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (fieldErrors.email) {
      setFieldErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleContactChange = (e) => {
    const value = e.target.value.replace(/[^\d\s]/g, "");
    setContactNumber(value);
    if (fieldErrors.contactNumber) {
      setFieldErrors((prev) => ({ ...prev, contactNumber: "" }));
    }
  };

  const handleAgeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 3) {
      setAge(value);
    }
    if (fieldErrors.age) {
      setFieldErrors((prev) => ({ ...prev, age: "" }));
    }
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    if (fieldErrors.address) {
      setFieldErrors((prev) => ({ ...prev, address: "" }));
    }
  };

  const handleSkipVerification = () => {
    const confirmed = window.confirm(
      "You will not receive any loyalty points for this order.\n\nDo you want to continue without verification?",
    );
    if (confirmed) {
      onSkipVerification?.();
      onHide();
    }
  };

  if (!show) return null;

  return (
    <div className="verification-modal">
      <div className="verification-modal__overlay" onClick={onHide} />
      <div className="verification-modal__content">
        <button className="verification-modal__close" onClick={onHide}>
          <svg
            className="verification-modal__close-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {!emailSent ? (
          <>
            <div className="verification-modal__header">
              <h2 className="verification-modal__title">
                {isForLoyalty
                  ? "Verify to Earn Loyalty Points"
                  : "Verify Your Identity"}
              </h2>
            </div>

            {/* Scrollable container */}
            <div className="verification-modal__form-container">
              <form
                className="verification-modal__form"
                onSubmit={handleSubmit}
              >
                {/* User Name */}
                <div className="verification-modal__field">
                  <label className="verification-modal__label verification-modal__label--required">
                    User Name
                  </label>
                  <input
                    type="text"
                    className={`verification-modal__input ${
                      fieldErrors.userName
                        ? "verification-modal__input--error"
                        : ""
                    }`}
                    placeholder="Enter your user name"
                    value={userName}
                    onChange={handleNameChange}
                  />
                  {fieldErrors.userName && (
                    <span className="verification-modal__field-error">
                      {fieldErrors.userName}
                    </span>
                  )}
                </div>

                {/* Email Field */}
                <div className="verification-modal__field">
                  <label className="verification-modal__label verification-modal__label--required">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className={`verification-modal__input ${
                      fieldErrors.email
                        ? "verification-modal__input--error"
                        : ""
                    }`}
                    placeholder="Enter your email address"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  {fieldErrors.email && (
                    <span className="verification-modal__field-error">
                      {fieldErrors.email}
                    </span>
                  )}
                </div>

                {/* Contact Number Field */}
                <div className="verification-modal__field">
                  <label className="verification-modal__label verification-modal__label--required">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    className={`verification-modal__input ${
                      fieldErrors.contactNumber
                        ? "verification-modal__input--error"
                        : ""
                    }`}
                    placeholder="Enter your phone number"
                    value={contactNumber}
                    onChange={handleContactChange}
                  />
                  {fieldErrors.contactNumber && (
                    <span className="verification-modal__field-error">
                      {fieldErrors.contactNumber}
                    </span>
                  )}
                </div>

                {/* Age Field */}
                <div className="verification-modal__field">
                  <label className="verification-modal__label verification-modal__label--required">
                    Age
                  </label>
                  <input
                    type="text"
                    className={`verification-modal__input ${
                      fieldErrors.age ? "verification-modal__input--error" : ""
                    }`}
                    placeholder="Enter your age"
                    value={age}
                    onChange={handleAgeChange}
                  />
                  {fieldErrors.age && (
                    <span className="verification-modal__field-error">
                      {fieldErrors.age}
                    </span>
                  )}
                </div>

                {/* Address Field */}
                <div className="verification-modal__field">
                  <label className="verification-modal__label verification-modal__label--required">
                    Address
                  </label>
                  <textarea
                    className={`verification-modal__input verification-modal__input--textarea ${
                      fieldErrors.address
                        ? "verification-modal__input--error"
                        : ""
                    }`}
                    placeholder="Enter your address"
                    value={address}
                    onChange={handleAddressChange}
                    rows="2"
                  />
                  {fieldErrors.address && (
                    <span className="verification-modal__field-error">
                      {fieldErrors.address}
                    </span>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="verification-modal__error">
                    <svg
                      className="verification-modal__error-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`verification-modal__submit-btn ${
                    isLoading ? "verification-modal__submit-btn--loading" : ""
                  }`}
                  disabled={isLoading}
                >
                  Verify & Send Email
                </button>

                {/* Skip Button for Loyalty */}
                {isForLoyalty && (
                  <button
                    type="button"
                    className="verification-modal__skip-btn"
                    onClick={handleSkipVerification}
                    disabled={isLoading}
                  >
                    Continue Without Verification
                  </button>
                )}
              </form>
            </div>
          </>
        ) : (
          <div className="verification-modal__email-sent">
            <h2 className="verification-modal__email-sent-title">
              Check Your Email
            </h2>
            <p className="verification-modal__email-sent-text">
              We've sent a verification link to
            </p>
            <p className="verification-modal__email-sent-address">{email}</p>
            <p className="verification-modal__email-sent-instruction">
              Please click the link in the email to complete your verification
              and proceed to payment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationModal;
